import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Repeat, Tag } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEVERITIES, PROBLEM_STATUSES } from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Callout,
  Drawer,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonRows,
  Stat,
  StatusPill,
  problemStatusTone,
  severityTone,
} from '../../components/ui';
import { api, qs } from '../../lib/api';
import { formatDate, formatDateTime, humanise } from '../../lib/format';

/**
 * Problem intelligence.
 *
 * Three rules from the directive shape this page.
 *
 * §3 — raised, open and closed are three separate numbers. Showing only a total
 * hides whether the team is clearing work, which is exactly what the pilot's
 * "≥50 tags closed" target is measuring.
 *
 * §8 — a recurrence count never appears without the rule that produced it. The
 * rule comes from the server (which reads it from settings); this file must not
 * hardcode a threshold or re-derive recurrence.
 *
 * §5/§10 — age never implies overdue. Overdue is only ever judged against a due
 * date somebody set, and when nothing in scope has one the page says so.
 */

interface Counts {
  raised: number;
  stillOpen: number;
  open: number;
  inProgress: number;
  pendingVerification: number;
  closed: number;
  rejected: number;
  critical: number;
  overdue: number;
  noDueDate: number;
  slaConfigured: boolean;
}

interface Aging {
  buckets: Array<{ key: string; label: string; count: number }>;
  averageClosureHours: number | null;
  medianClosureHours: number | null;
  closedCount: number;
  oldestOpen: {
    id: string;
    tagNo: string;
    title: string;
    machineCode: string;
    detectedAt: string;
    ageDays: number;
  } | null;
  overdue: number;
  slaConfigured: boolean;
}

interface BreakdownRow {
  key: string | null;
  label: string;
  sublabel: string | null;
  raised: number;
  stillOpen: number;
  closed: number;
  critical: number;
  lastSeen: string | null;
}

interface CriticalRow {
  id: string;
  tagNo: string;
  title: string;
  machineCode: string;
  componentName: string | null;
  status: string;
  detectedAt: string;
  ageDays: number;
  assignedName: string | null;
  dueDate: string | null;
  overdue: boolean;
  verifiedAt: string | null;
}

interface ProblemAnalytics {
  range: { from: string; to: string };
  counts: Counts;
  aging: Aging;
  closure: {
    closedInPeriod: number;
    closedAllTime: number;
    averageClosureHours: number | null;
    medianClosureHours: number | null;
    periodFrom: string | null;
    periodTo: string | null;
  };
  breakdowns: Record<string, BreakdownRow[]>;
  critical: CriticalRow[];
  trend: Array<{ period: string; raised: number; closed: number }>;
}

interface RecurrenceRule {
  threshold: number;
  windowDays: number;
  groupBy: string;
  description: string;
  isDefault: boolean;
}

interface RecurringGroup {
  machineId: string;
  machineCode: string;
  machineName: string;
  groupBy: string;
  groupKey: string | null;
  groupLabel: string;
  occurrences: number;
  open: number;
  closed: number;
  firstSeen: string;
  lastSeen: string;
  topRootCause: string | null;
  oplNo: string | null;
  oplId: string | null;
  ruleDescription: string;
}

interface TagRow {
  id: string;
  tag_no: string;
  title: string;
  severity: string;
  status: string;
  machine_code: string;
  component_name: string | null;
  detected_at: string;
  due_date: string | null;
  closed_at: string | null;
  root_cause_category: string | null;
  assigned_name: string | null;
  detected_by_name: string;
}

const DIMENSIONS = [
  { value: 'machine', label: 'By machine' },
  { value: 'component', label: 'By component' },
  { value: 'rootCause', label: 'By root cause' },
  { value: 'severity', label: 'By severity' },
  { value: 'category', label: 'By category' },
  { value: 'status', label: 'By status' },
  { value: 'detectionSource', label: 'By detection source' },
  { value: 'checklistItem', label: 'By checklist task' },
  { value: 'assignedDept', label: 'By department' },
];

/** The pilot target from the proposal — shown as a target, never as an actual. */
const CLOSED_TARGET = 50;

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

function hoursLabel(hours: number | null): string {
  if (hours === null) return 'No data';
  if (hours < 48) return `${hours} h`;
  return `${Math.round((hours / 24) * 10) / 10} days`;
}

export function ProblemAnalyticsPage() {
  const [from, setFrom] = useState(isoDaysAgo(90));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [dimension, setDimension] = useState('machine');
  const [machineId, setMachineId] = useState('');
  const [severity, setSeverity] = useState('');
  const [status, setStatus] = useState('');
  const [detectionSource, setDetectionSource] = useState('');

  const [drill, setDrill] = useState<
    | { kind: 'bucket'; bucket: string; label: string }
    | { kind: 'overdue' }
    | { kind: 'group'; row: BreakdownRow }
    | { kind: 'recurring'; group: RecurringGroup }
    | null
  >(null);

  const range = { from: `${from}T00:00:00.000Z`, to: `${to}T23:59:59.999Z` };

  /**
   * One filter object, shared by every widget and by the drill-down. Directive
   * §4: a filter must never move one panel and leave the others behind.
   */
  const filters = {
    ...range,
    machineId: machineId || undefined,
    severity: severity || undefined,
    status: status || undefined,
    detectionSource: detectionSource || undefined,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['analytics', 'problems', filters, dimension],
    queryFn: () =>
      api.get<ProblemAnalytics>(
        `/api/analytics/problems${qs({ ...filters, by: dimension, granularity: 'week' })}`,
      ),
  });

  /**
   * Scope filters flow into recurrence, dates do not — the rule owns its own
   * look-back window and the server keeps it that way (directive §4 vs §9).
   */
  const recurrenceScope = {
    machineId: machineId || undefined,
    severity: severity || undefined,
    status: status || undefined,
    detectionSource: detectionSource || undefined,
  };

  const { data: recurring } = useQuery({
    queryKey: ['analytics', 'recurring', recurrenceScope],
    queryFn: () =>
      api.get<{ rule: RecurrenceRule; data: RecurringGroup[] }>(
        `/api/analytics/problems/recurring${qs(recurrenceScope)}`,
      ),
  });

  const { data: coverage } = useQuery({
    queryKey: ['analytics', 'recurring', 'coverage', recurrenceScope],
    queryFn: () =>
      api.get<{ groups: number; problemsInGroups: number; problemsExamined: number }>(
        `/api/analytics/problems/recurring/coverage${qs(recurrenceScope)}`,
      ),
  });

  const { data: machines } = useQuery({
    queryKey: ['machines', 'picker'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; code: string; name: string }> }>('/api/machines'),
  });

  // Drill-down queries. The recurring drawer uses its own endpoint because a
  // recurring group is defined by the rule's window, not by the page's dates.
  const { data: drillTags, isLoading: drillLoading } = useQuery({
    enabled: drill !== null && drill.kind !== 'recurring',
    queryKey: ['analytics', 'problems', 'drilldown', filters, drill],
    queryFn: () =>
      api.get<{ data: TagRow[] }>(
        `/api/analytics/problems/drilldown${qs({
          ...filters,
          bucket: drill?.kind === 'bucket' ? drill.bucket : undefined,
          overdueOnly: drill?.kind === 'overdue' ? true : undefined,
          ...(drill?.kind === 'group' ? groupFilter(dimension, drill.row) : {}),
        })}`,
      ),
  });

  const { data: recurringTags, isLoading: recurringTagsLoading } = useQuery({
    enabled: drill?.kind === 'recurring',
    queryKey: ['analytics', 'recurring', 'tags', drill],
    queryFn: () => {
      const group = drill?.kind === 'recurring' ? drill.group : null;
      return api.get<{ data: TagRow[] }>(
        `/api/analytics/problems/recurring/tags${qs({
          machineId: group?.machineId,
          groupBy: group?.groupBy,
          groupKey: group?.groupKey ?? undefined,
        })}`,
      );
    },
  });

  const counts = data?.counts;
  const aging = data?.aging;
  const rows = data?.breakdowns[dimension] ?? [];
  const tags = drill?.kind === 'recurring' ? recurringTags?.data : drillTags?.data;
  const tagsLoading = drill?.kind === 'recurring' ? recurringTagsLoading : drillLoading;

  return (
    <div>
      <PageHeader
        title="Problem intelligence"
        subtitle="What is going wrong, where, how long it has been open, and what keeps coming back."
      />

      <div className="space-y-4 p-4 lg:p-6">
        <Card className="p-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="From">
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
            <Field label="Machine">
              <Select value={machineId} onChange={(e) => setMachineId(e.target.value)}>
                <option value="">All machines</option>
                {machines?.data.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.code} — {m.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Severity">
              <Select value={severity} onChange={(e) => setSeverity(e.target.value)}>
                <option value="">Any severity</option>
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {humanise(s)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Status">
              <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Any status</option>
                {PROBLEM_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {humanise(s)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Detected during">
              <Select
                value={detectionSource}
                onChange={(e) => setDetectionSource(e.target.value)}
              >
                <option value="">Any source</option>
                <option value="DAILY">Daily CILT</option>
                <option value="WEEKLY">Weekly CILT</option>
                <option value="MONTHLY">Monthly CILT</option>
                <option value="EVERY_X_HOURS">Shift CILT</option>
                <option value="MANUAL">Raised by hand</option>
              </Select>
            </Field>
            <Field label="Break down">
              <Select value={dimension} onChange={(e) => setDimension(e.target.value)}>
                {DIMENSIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </Card>

        {isLoading || !counts || !aging ? (
          <SkeletonRows rows={4} />
        ) : counts.raised === 0 ? (
          <EmptyState
            icon={Tag}
            title="No Problem Tags in this range"
            message="Nothing matches the current filter. That is not zero problems — it is nothing recorded here."
          />
        ) : (
          <>
            {/* §3: raised, open and closed side by side, never one total. */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Stat label="Raised" value={counts.raised} hint="Detected in this period" />
              <Stat
                label="Still open"
                value={counts.stillOpen}
                tone={counts.stillOpen > 0 ? 'warn' : 'ok'}
                hint={`${counts.open} not started · ${counts.inProgress} in progress · ${counts.pendingVerification} awaiting verification`}
              />
              <Stat
                label="Closed"
                value={counts.closed}
                tone="ok"
                hint={`${data.closure.closedAllTime} closed all time`}
              />
              <Stat
                label="Critical open"
                value={counts.critical}
                tone={counts.critical > 0 ? 'alarm' : 'ok'}
              />
            </div>

            {counts.critical > 0 ? (
              <Card className="border-alarm-300">
                <CardHeader
                  title={`${counts.critical} critical problem${counts.critical === 1 ? '' : 's'} open`}
                  subtitle="Oldest first. Analytics cannot change these — use the Problem Tag itself."
                />
                <ul className="divide-y divide-steel-100">
                  {data.critical.map((row) => (
                    <li key={row.id}>
                      <Link
                        to={`/problems/${row.id}`}
                        className="block px-4 py-3 transition hover:bg-steel-50"
                      >
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <p className="text-sm font-bold text-steel-900">
                            <span className="font-mono text-alarm-700">{row.tagNo}</span>{' '}
                            {row.title}
                          </p>
                          <span className="flex items-center gap-2">
                            <Badge tone="alarm" icon={AlertTriangle}>
                              {row.ageDays}d old
                            </Badge>
                            <StatusPill
                              tone={problemStatusTone(row.status)}
                              label={humanise(row.status)}
                            />
                          </span>
                        </div>
                        <p className="mt-0.5 text-xs text-steel-600">
                          {row.machineCode} · {row.componentName ?? 'Machine level'} ·{' '}
                          {row.assignedName ?? 'Unassigned'}
                          {row.dueDate
                            ? row.overdue
                              ? ` · overdue since ${formatDate(row.dueDate)}`
                              : ` · due ${formatDate(row.dueDate)}`
                            : ' · no due date set'}
                          {row.verifiedAt ? ` · verified ${formatDate(row.verifiedAt)}` : ''}
                        </p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader
                  title="How long open problems have been open"
                  subtitle="Measured from when each tag was detected"
                />
                <ul className="divide-y divide-steel-100">
                  {aging.buckets.map((bucket) => (
                    <li
                      key={bucket.key}
                      className="flex items-center justify-between gap-3 px-4 py-2"
                    >
                      <span className="text-sm text-steel-700">{bucket.label}</span>
                      {bucket.count === 0 ? (
                        <span className="text-sm tabular-nums text-steel-400">0</span>
                      ) : (
                        <button
                          type="button"
                          className="text-sm font-semibold tabular-nums text-info-700 hover:underline"
                          onClick={() =>
                            setDrill({ kind: 'bucket', bucket: bucket.key, label: bucket.label })
                          }
                        >
                          {bucket.count}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="border-t border-steel-100 px-4 py-3">
                  {/* §5: age is not overdue. Say so when no SLA exists. */}
                  {aging.slaConfigured ? (
                    <p className="text-xs text-steel-600">
                      Overdue:{' '}
                      {aging.overdue === 0 ? (
                        <span className="font-semibold text-ok-700">none</span>
                      ) : (
                        <button
                          type="button"
                          className="font-semibold text-alarm-700 hover:underline"
                          onClick={() => setDrill({ kind: 'overdue' })}
                        >
                          {aging.overdue}
                        </button>
                      )}{' '}
                      · judged against the due date on each tag
                      {counts.noDueDate > 0
                        ? ` · ${counts.noDueDate} open tag(s) have no due date`
                        : ''}
                    </p>
                  ) : (
                    <p className="text-xs text-steel-600">
                      <span className="font-semibold">SLA: not configured.</span> No tag in scope
                      carries a due date, so nothing here can be called overdue — age alone does
                      not make a problem late.
                    </p>
                  )}
                  {aging.oldestOpen ? (
                    <p className="mt-2 text-xs text-steel-600">
                      Oldest open:{' '}
                      <Link
                        to={`/problems/${aging.oldestOpen.id}`}
                        className="font-semibold text-info-700 hover:underline"
                      >
                        {aging.oldestOpen.tagNo}
                      </Link>{' '}
                      on {aging.oldestOpen.machineCode} — {aging.oldestOpen.ageDays} days
                    </p>
                  ) : null}
                </div>
              </Card>

              <Card>
                <CardHeader
                  title="Closure"
                  subtitle="Calculated only from tags that were actually closed"
                />
                <dl className="px-4 py-2">
                  <Row label="Closed in this period">{data.closure.closedInPeriod}</Row>
                  <Row label="Average time to close">
                    {hoursLabel(data.closure.averageClosureHours)}
                  </Row>
                  <Row label="Median time to close">
                    {hoursLabel(data.closure.medianClosureHours)}
                  </Row>
                </dl>
                <div className="border-t border-steel-100 px-4 py-3">
                  {/*
                    §17: the target is stated next to what the period actually
                    contains, and an all-time figure passing 50 is never
                    presented as the pilot target having been met.
                  */}
                  <p className="text-xs text-steel-600">
                    <span className="font-semibold">Pilot target: ≥{CLOSED_TARGET} closed.</span>{' '}
                    This period: {data.closure.closedInPeriod}. All time:{' '}
                    {data.closure.closedAllTime}.
                  </p>
                  <p className="mt-1 text-xs text-steel-500">
                    Achievement is judged over the pilot window, not over whatever range is
                    selected here.
                  </p>
                </div>
              </Card>
            </div>

            {recurring && recurring.data.length > 0 ? (
              <Card>
                <CardHeader
                  title="Recurring issues"
                  subtitle={
                    /* §8: never a count without its rule. */
                    `Rule: ${recurring.rule.description}${recurring.rule.isDefault ? ' (default — configurable in settings)' : ''}`
                  }
                />
                {coverage ? (
                  <div className="border-b border-steel-100 px-4 py-2 text-xs text-steel-600">
                    {coverage.groups} recurring group(s) · {coverage.problemsInGroups} of{' '}
                    {coverage.problemsExamined} tags in the rule&apos;s {recurring.rule.windowDays}
                    -day window belong to one.
                  </div>
                ) : null}
                <ul className="divide-y divide-steel-100">
                  {recurring.data.map((group) => (
                    <li key={`${group.machineId}-${group.groupKey ?? 'none'}`}>
                      <button
                        type="button"
                        className="block w-full px-4 py-3 text-left transition hover:bg-steel-50"
                        onClick={() => setDrill({ kind: 'recurring', group })}
                      >
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <p className="text-sm font-bold text-steel-900">
                            {group.machineCode} · {group.groupLabel}
                          </p>
                          <Badge tone="warn" icon={Repeat}>
                            {group.occurrences} occurrences
                          </Badge>
                        </div>
                        <p className="mt-0.5 text-xs text-steel-600">
                          {group.open} open · {group.closed} closed · last seen{' '}
                          {formatDate(group.lastSeen)}
                          {group.topRootCause
                            ? ` · top root cause: ${humanise(group.topRootCause)}`
                            : ' · root cause not recorded'}
                        </p>
                        <p className="mt-0.5 text-xs text-steel-500">
                          {group.oplNo ? `Related OPL: ${group.oplNo}` : 'No related OPL'}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              </Card>
            ) : recurring ? (
              <Callout tone="ok" title="No recurring issues">
                Nothing meets the rule: {recurring.rule.description}.
              </Callout>
            ) : null}

            <Card>
              <CardHeader
                title={DIMENSIONS.find((d) => d.value === dimension)?.label ?? 'Breakdown'}
                subtitle="Most problems first. Every count opens the tags behind it."
              />
              {rows.length === 0 ? (
                <EmptyState icon={Tag} title="Nothing to group" message="No tags match." />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px] text-sm">
                    <thead>
                      <tr className="border-b border-steel-200 text-left text-xs tracking-wide text-steel-500 uppercase">
                        <th className="px-4 py-2 font-semibold">Group</th>
                        <th className="px-3 py-2 text-right font-semibold">Raised</th>
                        <th className="px-3 py-2 text-right font-semibold">Open</th>
                        <th className="px-3 py-2 text-right font-semibold">Closed</th>
                        <th className="px-3 py-2 text-right font-semibold">Critical</th>
                        <th className="px-4 py-2 text-right font-semibold">Last seen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-steel-100">
                      {rows.map((row) => (
                        <tr key={row.key ?? row.label} className="hover:bg-steel-50">
                          <td className="px-4 py-2">
                            {dimension === 'machine' && row.key ? (
                              <Link
                                to={`/machines/${row.key}/history`}
                                className="font-semibold text-info-700 hover:underline"
                              >
                                {row.label}
                              </Link>
                            ) : (
                              <span className="font-semibold text-steel-900">
                                {humanise(row.label)}
                              </span>
                            )}
                            {row.sublabel ? (
                              <span className="block text-xs text-steel-500">{row.sublabel}</span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <button
                              type="button"
                              className="font-semibold text-info-700 hover:underline"
                              onClick={() => setDrill({ kind: 'group', row })}
                            >
                              {row.raised}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{row.stillOpen}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{row.closed}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {row.critical > 0 ? (
                              <span className="font-semibold text-alarm-600">{row.critical}</span>
                            ) : (
                              <span className="text-steel-400">0</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right text-xs text-steel-500">
                            {row.lastSeen ? formatDate(row.lastSeen) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {data.trend.length > 1 ? (
              <Card>
                <CardHeader
                  title="Raised and closed over time"
                  subtitle="Only periods with recorded activity appear — gaps are not filled in."
                />
                <div className="overflow-x-auto px-4 py-3">
                  <div className="flex min-w-max items-end gap-3">
                    {data.trend.map((point) => {
                      const peak = Math.max(
                        ...data.trend.map((p) => Math.max(p.raised, p.closed)),
                        1,
                      );
                      return (
                        <div key={point.period} className="flex w-14 flex-col items-center gap-1">
                          <div className="flex h-24 items-end gap-1">
                            <div
                              className="w-4 rounded-t bg-warn-500"
                              style={{ height: `${(point.raised / peak) * 96}px` }}
                              title={`${point.raised} raised`}
                            />
                            <div
                              className="w-4 rounded-t bg-ok-600"
                              style={{ height: `${(point.closed / peak) * 96}px` }}
                              title={`${point.closed} closed`}
                            />
                          </div>
                          <span className="text-[10px] text-steel-500">{point.period}</span>
                          <span className="text-[10px] tabular-nums text-steel-400">
                            {point.raised}/{point.closed}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-xs text-steel-500">
                    <span className="inline-block size-2 rounded-sm bg-warn-500" /> raised{' '}
                    <span className="ml-3 inline-block size-2 rounded-sm bg-ok-600" /> closed
                  </p>
                </div>
              </Card>
            ) : null}
          </>
        )}
      </div>

      <Drawer
        open={drill !== null}
        onClose={() => setDrill(null)}
        width="lg"
        title={drawerTitle(drill)}
        subtitle={
          drill?.kind === 'recurring'
            ? drill.group.ruleDescription
            : `${from} to ${to}${machineId ? ' · filtered by machine' : ''}`
        }
      >
        {tagsLoading ? (
          <SkeletonRows rows={5} />
        ) : !tags?.length ? (
          <EmptyState icon={Tag} title="No tags" message="Nothing matches this selection." />
        ) : (
          <ul className="divide-y divide-steel-100">
            {tags.map((tag) => (
              <li key={tag.id}>
                {/* Opens the existing Problem Tag page — not a second detail UI. */}
                <Link to={`/problems/${tag.id}`} className="block py-3 transition hover:bg-steel-50">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-sm font-semibold text-steel-900">
                      <span className="font-mono text-info-700">{tag.tag_no}</span> {tag.title}
                    </p>
                    <span className="flex items-center gap-1.5">
                      <Badge tone={severityTone(tag.severity)}>{humanise(tag.severity)}</Badge>
                      <StatusPill
                        tone={problemStatusTone(tag.status)}
                        label={humanise(tag.status)}
                      />
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-steel-600">
                    {tag.machine_code} · {tag.component_name ?? 'Machine level'} · detected{' '}
                    {formatDateTime(tag.detected_at)} by {tag.detected_by_name}
                  </p>
                  <p className="mt-0.5 text-xs text-steel-500">
                    {tag.assigned_name ? `Assigned to ${tag.assigned_name}` : 'Unassigned'}
                    {tag.due_date ? ` · due ${formatDate(tag.due_date)}` : ' · no due date'}
                    {tag.closed_at ? ` · closed ${formatDate(tag.closed_at)}` : ''}
                    {' · '}
                    {/* §14: an unrecorded root cause stays unrecorded. */}
                    {tag.root_cause_category
                      ? `root cause: ${humanise(tag.root_cause_category)}`
                      : 'root cause not recorded'}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Drawer>
    </div>
  );
}

function drawerTitle(
  drill:
    | { kind: 'bucket'; label: string }
    | { kind: 'overdue' }
    | { kind: 'group'; row: BreakdownRow }
    | { kind: 'recurring'; group: RecurringGroup }
    | null,
): string {
  if (!drill) return '';
  switch (drill.kind) {
    case 'bucket':
      return `Open ${drill.label}`;
    case 'overdue':
      return 'Overdue problems';
    case 'group':
      return drill.row.label;
    case 'recurring':
      return `${drill.group.machineCode} · ${drill.group.groupLabel}`;
  }
}

/** Turn a clicked breakdown row back into the filter that produced it. */
function groupFilter(dimension: string, row: BreakdownRow): Record<string, string | undefined> {
  const key = row.key ?? undefined;
  switch (dimension) {
    case 'machine':
      return { machineId: key };
    case 'component':
      return { componentId: key };
    case 'rootCause':
      return { rootCause: key };
    case 'severity':
      return { severity: key };
    case 'category':
      return { category: key };
    case 'status':
      return { status: key };
    case 'detectionSource':
      return { detectionSource: key };
    case 'checklistItem':
      return { itemId: key };
    case 'assignedDept':
      return { assignedDept: key };
    default:
      return {};
  }
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-steel-100 py-2 last:border-0">
      <dt className="text-sm text-steel-600">{label}</dt>
      <dd className="text-sm font-semibold tabular-nums text-steel-900">{children}</dd>
    </div>
  );
}
